import type { FC } from "react";

import { Button, Group, Overlay, Paper, ScrollArea, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconArrowLeft, IconNotes } from "@tabler/icons-react";
import dayjs from "dayjs";

import sharedStyles from "@/common-ui/sharedStyles.module.css";

import type { TrainingSession } from "../../types";

import { BehaviorsChecklist } from "./BehaviorsChecklist";

export interface EvaluationResultsOverlayProps {
    /** Display heading for the profile whose results are shown (e.g. "Block 1.2"). */
    title: string;
    /** The expert's training session for this profile; its `evaluationDetails` drive the summary. */
    session: TrainingSession;
    /** Return to the profiles accordion. */
    onBack: () => void;
    /** Open the full transcript (AscendEvaluationPage) for the last attempt. */
    onViewTranscript: () => void;
}

const formatAttemptDate = (timestamp?: string): string =>
    timestamp ? dayjs(timestamp).format("MMM D, YYYY h:mma") : "Unknown";

/**
 * Summary overlay shown when the expert clicks "View results" on a profile they've already attempted.
 * Surfaces the last attempt's overview, a behaviors checklist, the number of attempts, and the last
 * attempt date. "Back to profiles" returns to the accordion; "View transcript" opens the full
 * AscendEvaluationPage for the last attempt. Distinct from the post-call `trigger-evaluation-results`
 * flow, which renders AscendEvaluationPage directly.
 */
export const EvaluationResultsOverlay: FC<EvaluationResultsOverlayProps> = ({
    title,
    session,
    onBack,
    onViewTranscript,
}) => {
    const attempts = session.evaluationDetails.length;
    const latest = session.evaluationDetails[0];

    return (
        <Overlay color="var(--exwo-color-app-bg)" backgroundOpacity={0.97} data-testid="evaluation-results-overlay">
            <ScrollArea h="100%" type="hover" scrollbarSize={8}>
                <Stack gap="1.5rem" maw={880} mx="auto" p="1.5rem" className={sharedStyles.animateScaleIn}>
                    <Group justify="space-between" align="center">
                        <Button
                            variant="subtle"
                            color="gray"
                            leftSection={<IconArrowLeft size={16} />}
                            onClick={onBack}
                        >
                            Back to Learning Blocks
                        </Button>
                        <Button
                            color="var(--mantine-color-brand-5)"
                            onClick={onViewTranscript}
                            leftSection={IconNotes && <IconNotes size={16} />}
                            disabled={!latest?.SessionId}
                        >
                            View transcript
                        </Button>
                    </Group>

                    <Text fw={700} fz="1.75rem" c="var(--mantine-color-gray-0)">
                        Assessment: {title}
                    </Text>

                    {latest?.Overview && (
                        <Stack gap="1rem">
                            <Text fz="0.75rem" fs="italic" c="var(--mantine-color-gray-5)">
                                Total Attempts: {attempts}
                            </Text>
                            <Text fz="0.75rem" fs="italic" c="var(--mantine-color-gray-5)">
                                Last Attempted: {formatAttemptDate(latest?.Timestamp)}
                            </Text>
                            <Text fw={700} size="xl" c="var(--mantine-color-gray-0)">
                                Overview
                            </Text>
                            <Text c="var(--mantine-color-gray-2)" lh="150%">
                                {latest.Overview}
                            </Text>
                        </Stack>
                    )}
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                        <Stack gap="1rem">
                            <Text fw={700} size="xl" c="var(--mantine-color-gray-0)">
                                Assessment Results
                            </Text>
                            <Paper withBorder radius="md" bg="var(--mantine-color-dark-5)" p="0.5rem">
                                <BehaviorsChecklist behaviors={latest?.Body ?? []} />
                            </Paper>
                        </Stack>
                    </SimpleGrid>
                </Stack>
            </ScrollArea>
        </Overlay>
    );
};

export default EvaluationResultsOverlay;
